// AUTO-GENERATED SEED — review before trusting. Rename keys (index→list/detail),
// refine params, confirm caller flags, add opt-in response types.
import { responseType } from '@auctogon/core/api/registry';
import { defineAdminRoute } from './define-admin-route';
// Type-only imports (erased at build) — keep the registry client-bundle-safe.
import type { DocumentNumberFormatsPayload } from '../../schemas/responses/document-number-format';
import type { NotificationLogDto, NotificationLogListPayload } from '../../schemas/responses/notification-log';
import type { TenantCloseStagesPayload } from '../../schemas/responses/tenant-close-stages';
import type { TenantPaymentTermsPayload } from '../../schemas/responses/tenant-payment-terms';
import type {
  TenantDomainDto,
  TenantDomainListPayload,
} from '../../schemas/responses/tenant-domain';

// Every route here is the platform tier (ADR 031 — Config Ownership): a super
// admin passes, and no tenant role does — not even an Admin at their own
// tenant. The gate enforces it from the declaration (BL-1833 PR 4b).
const PLATFORM_WIDE = { from: 'none', reason: 'platform-wide — spans every tenant' } as const;
// A tenant Admin gets 403 for their OWN tenant's domains too, so the tenant is
// not an access boundary here and `record` would add nothing but a 404 on an
// archived tenant the domains screen still lists.
const DOMAINS = { superAdmin: 'custom domains are platform config' } as const;
// BL-2030 (MN8): a house's document-number format is set by Auctogon, never by
// house staff — a tenant Admin gets 403 even for their own house.
const NUMBER_FORMATS = {
  superAdmin: 'document number formats are platform config (ADR 031 — Config Ownership, MN8)',
} as const;
// BL-2281 (PRJ-89 D6): a house's Closed label and closing-stage names are set
// by Auctogon, never by house staff — a tenant Admin gets 403 even for their own
// house; every staff role reads them off the session bootstrap instead.
const CLOSE_STAGES = {
  superAdmin: 'closing stages are platform config (ADR 031 — Config Ownership)',
} as const;
// BL-2499 (SMONEY-6): a house's seller payout days and invoice due days are set
// by Auctogon, never by house staff — a tenant Admin gets 403 even for their own
// house (Dan Saper, 2026-10-13).
const PAYMENT_TERMS = {
  superAdmin: 'payment terms are platform config (ADR 031 — Config Ownership)',
} as const;

export const super_admin = {
  // BL-1820 — the Delivery log (ADR 027 — Response Contract).
  notification_logs: defineAdminRoute({
      path: '/api/super-admin/notification-logs',
      params: {},
      methods: {
        GET: {
          grant: { superAdmin: 'the platform delivery log spans every tenant' },
          tenant: PLATFORM_WIDE,
          response: responseType<NotificationLogListPayload>(),
        },
      },
    }),
  // BL-1820 — one delivery with its bodies and metadata, for the log's slide-out.
  notification_log: defineAdminRoute({
      path: '/api/super-admin/notification-logs/:id',
      params: { id: '' },
      methods: {
        GET: {
          grant: { superAdmin: 'the platform delivery log spans every tenant' },
          tenant: PLATFORM_WIDE,
          response: responseType<NotificationLogDto>(),
        },
      },
    }),
  notification_templates: {
    id: defineAdminRoute({
        path: '/api/super-admin/notification-templates/:id',
        params: { id: '' },
        // GET retired (BL-1833 PR 4b): the templates screen reads the list and
        // never fetched one template by id.
        methods: {
          PATCH: { grant: { superAdmin: 'global and per-tenant template overrides are platform config' }, tenant: PLATFORM_WIDE },
          DELETE: { grant: { superAdmin: 'global and per-tenant template overrides are platform config' }, tenant: PLATFORM_WIDE },
        },
      }),
    index: defineAdminRoute({
        path: '/api/super-admin/notification-templates',
        params: {},
        // POST's body `tenantId` names the override's target, not the caller's
        // tenant — a super admin writes for any tenant, or none (global).
        methods: {
          GET: { grant: { superAdmin: 'global and per-tenant template overrides are platform config' }, tenant: PLATFORM_WIDE },
          POST: { grant: { superAdmin: 'global and per-tenant template overrides are platform config' }, tenant: PLATFORM_WIDE },
        },
      }),
    // `preview` moved to the top-level `notification_templates` registry (BUG-828)
    // — it is reachable by auction editors, not just super-admins.
  },
  // BL-1763 — Super Admin → Tenants → Tenant domains (ADR 027 — Response Contract).
  tenant_domains: {
    index: defineAdminRoute({
      path: '/api/super-admin/tenants/:id/domains',
      params: { id: '' },
      methods: {
        GET: { grant: DOMAINS, tenant: PLATFORM_WIDE, response: responseType<TenantDomainListPayload>() },
        POST: { grant: DOMAINS, tenant: PLATFORM_WIDE, response: responseType<TenantDomainDto>() },
      },
    }),
    detail: defineAdminRoute({
      path: '/api/super-admin/tenants/:id/domains/:domainId',
      params: { id: '', domainId: '' },
      // PATCH promotes to primary and returns the promoted row; DELETE
      // soft-deletes and returns no data payload.
      methods: {
        PATCH: { grant: DOMAINS, tenant: PLATFORM_WIDE, response: responseType<TenantDomainDto>() },
        DELETE: { grant: DOMAINS, tenant: PLATFORM_WIDE },
      },
    }),
  },
  // BL-2030 — Super Admin → Tenants → Document numbering (ADR 027 — Response Contract).
  document_number_formats: defineAdminRoute({
    path: '/api/super-admin/tenants/:id/document-number-formats',
    params: { id: '' },
    methods: {
      GET: { grant: NUMBER_FORMATS, tenant: PLATFORM_WIDE, response: responseType<DocumentNumberFormatsPayload>() },
      PUT: { grant: NUMBER_FORMATS, tenant: PLATFORM_WIDE, response: responseType<DocumentNumberFormatsPayload>() },
    },
  }),
  // BL-2281 — Super Admin → Tenants → Closing stages (ADR 027 — Response Contract).
  tenant_close_stages: defineAdminRoute({
    path: '/api/super-admin/tenants/:id/closing-stages',
    params: { id: '' },
    methods: {
      GET: { grant: CLOSE_STAGES, tenant: PLATFORM_WIDE, response: responseType<TenantCloseStagesPayload>() },
      PUT: { grant: CLOSE_STAGES, tenant: PLATFORM_WIDE, response: responseType<TenantCloseStagesPayload>() },
    },
  }),
  // BL-2499 — Super Admin → Tenants → Payment terms (ADR 027 — Response Contract).
  tenant_payment_terms: defineAdminRoute({
    path: '/api/super-admin/tenants/:id/payment-terms',
    params: { id: '' },
    methods: {
      GET: { grant: PAYMENT_TERMS, tenant: PLATFORM_WIDE, response: responseType<TenantPaymentTermsPayload>() },
      PUT: { grant: PAYMENT_TERMS, tenant: PLATFORM_WIDE, response: responseType<TenantPaymentTermsPayload>() },
    },
  }),
};
